import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getERPSummary } from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, TrendingDown, Wallet, Users, 
  ArrowRight, DollarSign, PieChart, Loader2
} from 'lucide-react';

export default function ERPDashboard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const summaryRes = await getERPSummary(String(selectedMonth).padStart(2, '0'), String(selectedYear));
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error('Failed to load ERP data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <AdminLayout title="ERP Dashboard">
      <div className="space-y-6" data-testid="erp-dashboard">
        {/* Header with Date Filter */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Financial Overview</h1>
            <p className="text-[#888] text-sm">Track income, expenses, and client billing</p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="bg-[#121212] border border-[#333] text-white px-4 py-2 text-sm outline-none"
            >
              {months.map((month, idx) => (
                <option key={idx} value={idx + 1}>{month}</option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="bg-[#121212] border border-[#333] text-white px-4 py-2 text-sm outline-none"
            >
              {[2024, 2025, 2026, 2027].map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Bank Balance - Auto Calculated */}
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6 relative">
                <div className="absolute top-4 right-4">
                  <span className="text-[10px] text-[#555] bg-[#1F1F1F] px-2 py-1 rounded">AUTO</span>
                </div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center">
                    <Wallet className="w-5 h-5 text-blue-500" />
                  </div>
                  <span className="text-[#888] text-sm">Bank Balance</span>
                </div>
                <p className={`font-syne text-2xl font-bold ${(summary?.bank_balance || 0) >= 0 ? 'text-white' : 'text-red-500'}`}>
                  {formatCurrency(summary?.bank_balance)}
                </p>
                <p className="text-[#555] text-xs mt-2">Total Income - Total Expenses</p>
              </div>

              {/* Total Income */}
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                  <span className="text-[#888] text-sm">Income ({months[selectedMonth - 1]})</span>
                </div>
                <p className="font-syne text-2xl font-bold text-green-500">
                  {formatCurrency(summary?.total_income)}
                </p>
              </div>

              {/* Total Expense */}
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  </div>
                  <span className="text-[#888] text-sm">Expenses ({months[selectedMonth - 1]})</span>
                </div>
                <p className="font-syne text-2xl font-bold text-red-500">
                  {formatCurrency(summary?.total_expense)}
                </p>
              </div>

              {/* Net Profit */}
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center ${summary?.net_profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <DollarSign className={`w-5 h-5 ${summary?.net_profit >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                  </div>
                  <span className="text-[#888] text-sm">Net Profit</span>
                </div>
                <p className={`font-syne text-2xl font-bold ${summary?.net_profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {formatCurrency(summary?.net_profit)}
                </p>
              </div>
            </div>

            {/* Client Stats & Expected Billing */}
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-syne font-bold text-white">Clients Overview</h3>
                  <Link to="/admin/erp/clients" className="text-[#ff5a1f] text-sm flex items-center gap-1 hover:gap-2 transition-all">
                    Manage <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-[#121212]">
                    <p className="font-syne text-3xl font-bold text-white">{summary?.total_clients || 0}</p>
                    <p className="text-[#888] text-xs">Total Clients</p>
                  </div>
                  <div className="text-center p-4 bg-[#121212]">
                    <p className="font-syne text-3xl font-bold text-green-500">{summary?.active_clients || 0}</p>
                    <p className="text-[#888] text-xs">Active</p>
                  </div>
                  <div className="text-center p-4 bg-[#121212]">
                    <p className="font-syne text-3xl font-bold text-[#555]">{(summary?.total_clients || 0) - (summary?.active_clients || 0)}</p>
                    <p className="text-[#888] text-xs">Inactive</p>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-[#ff5a1f]/10 border border-[#ff5a1f]/20">
                  <p className="text-[#888] text-sm">Expected Monthly Billing</p>
                  <p className="font-syne text-2xl font-bold text-[#ff5a1f]">
                    {formatCurrency(summary?.expected_monthly_billing)}
                  </p>
                </div>
              </div>

              {/* Income by Category */}
              <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-syne font-bold text-white">Income by Category</h3>
                  <Link to="/admin/erp/income" className="text-[#ff5a1f] text-sm flex items-center gap-1 hover:gap-2 transition-all">
                    View All <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
                {summary?.income_by_category && Object.keys(summary.income_by_category).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(summary.income_by_category).map(([category, amount]) => (
                      <div key={category} className="flex items-center justify-between">
                        <span className="text-[#888] text-sm">{category}</span>
                        <span className="text-white font-medium">{formatCurrency(amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[#555] text-center py-8">No income recorded this month</p>
                )}
              </div>
            </div>

            {/* Expense by Category */}
            <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-syne font-bold text-white">Expenses by Category</h3>
                <Link to="/admin/erp/expenses" className="text-[#ff5a1f] text-sm flex items-center gap-1 hover:gap-2 transition-all">
                  View All <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              {summary?.expense_by_category && Object.keys(summary.expense_by_category).length > 0 ? (
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(summary.expense_by_category).map(([category, amount]) => (
                    <div key={category} className="p-4 bg-[#121212] border border-[#1F1F1F]">
                      <p className="text-[#888] text-sm mb-1">{category}</p>
                      <p className="text-red-500 font-bold">{formatCurrency(amount)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#555] text-center py-8">No expenses recorded this month</p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link
                to="/admin/erp/income"
                className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/20 hover:border-green-500 transition-all"
              >
                <TrendingUp className="w-5 h-5 text-green-500" />
                <span className="text-white text-sm">Add Income</span>
              </Link>
              <Link
                to="/admin/erp/expenses"
                className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 hover:border-red-500 transition-all"
              >
                <TrendingDown className="w-5 h-5 text-red-500" />
                <span className="text-white text-sm">Add Expense</span>
              </Link>
              <Link
                to="/admin/erp/clients"
                className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 hover:border-blue-500 transition-all"
              >
                <Users className="w-5 h-5 text-blue-500" />
                <span className="text-white text-sm">Manage Clients</span>
              </Link>
              <Link
                to="/admin/erp/categories"
                className="flex items-center gap-3 p-4 bg-purple-500/10 border border-purple-500/20 hover:border-purple-500 transition-all"
              >
                <PieChart className="w-5 h-5 text-purple-500" />
                <span className="text-white text-sm">Categories</span>
              </Link>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
